import React, { useEffect, useState } from "react";
import { QrCode, FileSignature, MapPin, Sparkles, Play, X } from "lucide-react";

/**
 * Sezione "Come lo usano in azienda" della landing.
 * Mostra 3 storie reali (cantiere, ufficio, retail) con foto + mockup smartphone
 * e UI ToolBadge che si "anima" come fosse un video.
 *
 * Quando il video Sora 2 sarà pronto (file in /static/videos/toolbadge_landing.mp4),
 * verrà mostrato in cima alla sezione invece del cover image, con play overlay.
 *
 * Per attivare il video:  passa la prop `videoUrl` (es. dal backend o env)
 */

const VIDEO_PATH = "/videos/toolbadge_landing.mp4"; // servito da frontend public/

const STORIES = [
  {
    id: "cantiere",
    photo: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=900&q=80",
    role: "Edilizia",
    name: "Marco · Capocantiere",
    quote: "I miei operai timbrano col QR appena arrivano in cantiere. Niente più fogli, e l'app mi avvisa subito se qualcuno è fuori area.",
    icon: QrCode,
    accent: "Check-in QR geolocalizzato",
    badge: "Edili Costruzioni SRL · 45 dipendenti",
  },
  {
    id: "ufficio",
    photo: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=900&q=80",
    role: "Servizi",
    name: "Giulia · HR Manager",
    quote: "Le buste paga e i contratti firmati col dito direttamente dallo smartphone. I miei collaboratori amano la semplicità.",
    icon: FileSignature,
    accent: "Firma digitale touch",
    badge: "Studio Bianchi & Associati · 18 dipendenti",
  },
  {
    id: "retail",
    photo: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=900&q=80",
    role: "Commercio",
    name: "Davide · Titolare",
    quote: "Tre negozi, sedici commesse, mille cambi turno. Ora vedo tutto da un'unica dashboard mentre sono in giro.",
    icon: MapPin,
    accent: "Multi-sede + trasferte GPS",
    badge: "Boutique Costa · 3 punti vendita",
  },
];

// Mini mockup UI animata che simula la timbratura
function PhoneMockup() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep((s) => (s + 1) % 4), 2200);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="relative mx-auto" style={{ width: 240 }}>
      {/* Phone frame */}
      <div className="rounded-[32px] bg-[#0A0A0A] p-3 shadow-2xl shadow-black/40 relative">
        <div className="absolute top-3 left-1/2 -translate-x-1/2 w-20 h-5 bg-[#0A0A0A] rounded-b-2xl z-10"/>
        <div className="bg-gradient-to-br from-[#FFFAF5] to-white rounded-[24px] aspect-[9/19] overflow-hidden p-3 pt-8 relative">
          {/* Header */}
          <div className="flex items-center gap-2 mb-3 px-1">
            <div className="w-7 h-7 rounded-lg bg-[#FF7A29] flex items-center justify-center text-white text-[9px] font-black">α</div>
            <div className="text-[10px] font-semibold text-[#0A0A0A]">Tool Badge</div>
          </div>

          {/* Animated content per step */}
          <div className="space-y-2.5">
            <div className={`bg-white rounded-xl p-2.5 border border-[#EDE5D8] transition-all duration-700 ${step === 0 ? "scale-100 opacity-100" : "scale-95 opacity-50"}`}>
              <div className="text-[8px] uppercase tracking-widest text-[#999]">Posizione</div>
              <div className="text-[10px] font-bold text-[#0A0A0A] flex items-center gap-1 mt-0.5">
                <MapPin className="w-2.5 h-2.5 text-[#FF7A29]"/> Sede Milano · 48m
              </div>
            </div>

            <div className={`bg-white rounded-xl p-2.5 border border-[#EDE5D8] transition-all duration-700 ${step === 1 ? "scale-100 opacity-100" : "scale-95 opacity-50"}`}>
              <div className="aspect-square rounded-lg bg-[#0A0A0A] flex items-center justify-center">
                <QrCode className="w-12 h-12 text-white"/>
              </div>
              <div className="text-[9px] text-center mt-1.5 font-semibold">Inquadra il QR</div>
            </div>

            <div className={`bg-gradient-to-r from-[#FF7A29] to-[#E85D00] rounded-xl p-2.5 text-white transition-all duration-700 ${step === 2 ? "scale-100 opacity-100" : "scale-95 opacity-50"}`}>
              <div className="text-[8px] uppercase tracking-widest opacity-90">Check-in</div>
              <div className="text-xs font-bold flex items-center gap-1 mt-0.5">✓ Ingresso registrato</div>
              <div className="text-[9px] opacity-80 mt-0.5">08:42 · Marco Rossi</div>
            </div>

            <div className={`bg-[#0A0A0A] rounded-xl p-2.5 text-white transition-all duration-700 ${step === 3 ? "scale-100 opacity-100" : "scale-95 opacity-50"}`}>
              <div className="text-[8px] uppercase tracking-widest text-[#FF7A29]">Documenti da firmare</div>
              <div className="text-[10px] font-bold mt-1">Contratto integrativo</div>
              <div className="mt-1.5 h-6 bg-white rounded-md flex items-end pb-1 px-1.5">
                <svg viewBox="0 0 60 16" className="w-full h-4">
                  <path d="M2 12 Q 8 4 14 10 T 26 8 T 38 12 T 50 6 L 56 10" stroke="#FF7A29" strokeWidth="1.5" fill="none" strokeLinecap="round" className={step === 3 ? "anim-stroke" : ""}/>
                </svg>
              </div>
            </div>
          </div>

          {/* Step indicator */}
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className={`h-1 rounded-full transition-all duration-500 ${step === i ? "w-5 bg-[#FF7A29]" : "w-1.5 bg-[#EDE5D8]"}`}/>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes drawStroke {
          from { stroke-dasharray: 0 200; }
          to { stroke-dasharray: 200 0; }
        }
        .anim-stroke {
          stroke-dasharray: 200;
          stroke-dashoffset: 0;
          animation: drawStroke 1.4s ease-out forwards;
        }
      `}</style>
    </div>
  );
}

export default function HowTheyUseIt() {
  const [hasVideo, setHasVideo] = useState(false);
  const [playing, setPlaying] = useState(false);

  // Verifica se il video è disponibile (HEAD request al public/)
  useEffect(() => {
    fetch(VIDEO_PATH, { method: "HEAD" })
      .then((r) => setHasVideo(r.ok))
      .catch(() => setHasVideo(false));
  }, []);

  const videoUrl = VIDEO_PATH;

  return (
    <section className="bg-white relative overflow-hidden" data-testid="how-they-use">
      <div className="orb orb-orange" style={{ width: 700, height: 700, top: -300, left: -200, opacity: 0.18 }}/>
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-28">
        {/* Header */}
        <div className="grid md:grid-cols-12 gap-8 mb-14 items-end">
          <div className="md:col-span-8">
            <div className="inline-flex items-center gap-2 bg-[#FF7A29]/10 text-[#E85D00] text-xs font-semibold uppercase tracking-[0.18em] px-4 py-2 rounded-full mb-5">
              <Sparkles className="w-3.5 h-3.5"/> Come lo usano in azienda
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              Cantieri, uffici, negozi.<br/>
              <span className="text-[#757575]">Una sola piattaforma per tutti.</span>
            </h2>
          </div>
          <div className="md:col-span-4 text-sm text-[#555] md:text-right">
            Tre storie italiane, tre settori diversi.<br/>Stessa app, stessa semplicità.
          </div>
        </div>

        {/* Video / Hero visual */}
        <div className="relative rounded-[28px] overflow-hidden border border-[#EDE5D8] mb-12 bg-[#0A0A0A] aspect-[16/9] md:aspect-[21/9]" data-testid="how-they-use-video">
          {hasVideo ? (
            <>
              <video
                src={videoUrl}
                autoPlay={playing}
                muted
                loop
                playsInline
                controls={playing}
                className="w-full h-full object-cover"
                data-testid="marketing-video"
              />
              {!playing && (
                <button
                  onClick={() => setPlaying(true)}
                  data-testid="play-marketing-video"
                  className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors group"
                >
                  <span className="w-20 h-20 rounded-full bg-[#FF7A29] flex items-center justify-center group-hover:scale-110 transition-transform shadow-2xl shadow-orange-500/40">
                    <Play className="w-9 h-9 text-white ml-1" fill="currentColor"/>
                  </span>
                </button>
              )}
            </>
          ) : (
            // Fallback elegante (collage di foto + mockup) finché il video AI non è generato
            <div className="grid grid-cols-3 h-full" data-testid="how-they-use-fallback">
              {STORIES.map((s) => (
                <div key={s.id} className="relative overflow-hidden group">
                  <img
                    src={s.photo}
                    alt={s.role}
                    loading="lazy"
                    className="w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent"/>
                  <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7 text-white">
                    <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#FF7A29] font-bold mb-1.5">
                      <s.icon className="w-3 h-3"/> {s.role}
                    </div>
                    <div className="font-display text-lg md:text-xl leading-tight">{s.accent}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Stories grid + Phone mockup */}
        <div className="grid md:grid-cols-12 gap-6 md:gap-8 items-center">
          <div className="md:col-span-7 grid sm:grid-cols-1 lg:grid-cols-3 gap-4">
            {STORIES.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.id} className="card-smart p-5 md:p-6 hover:shadow-xl transition-all hover:-translate-y-1" data-testid={`story-${s.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-10 h-10 rounded-xl bg-[#FFF0E3] flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#FF7A29]"/>
                    </div>
                    <span className="text-[9px] uppercase tracking-widest text-[#FF7A29] font-bold bg-[#FFF0E3] px-2 py-1 rounded-full">{s.role}</span>
                  </div>
                  <p className="text-sm md:text-[15px] text-[#0A0A0A] leading-relaxed font-medium">"{s.quote}"</p>
                  <div className="mt-4 pt-4 border-t border-[#EDE5D8]">
                    <div className="text-sm font-bold text-[#0A0A0A]">{s.name}</div>
                    <div className="text-[11px] text-[#757575] mt-0.5">{s.badge}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="md:col-span-5 hidden md:flex flex-col items-center">
            <PhoneMockup/>
            <div className="mt-6 text-center max-w-xs">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF7A29] font-bold mb-2">In tempo reale</div>
              <h3 className="font-display text-xl text-[#0A0A0A] leading-tight">Dal QR alla firma in 4 tap.</h3>
              <p className="text-xs text-[#757575] mt-2 leading-relaxed">Compatibile con qualsiasi smartphone — niente download, tutto via browser sicuro.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
